import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Leaf, LogOut, Send } from "lucide-react";
import { Panel, Field, TextInput, TextArea, Btn, Modal, EmptyState } from "../components/ui";
import { StatusPill } from "../components/CbamSupplierRequests";
import LangToggle from "../components/LangToggle";
import { useI18n } from "../i18n";
import { useAuth, formatApiError } from "../auth";
import { CL } from "../api";

const EMPTY = { installation_name: "", installation_country: "", direct: "", indirect: "", carbon_price_paid: "", verifier: "", verification_ref: "", comment: "" };

const SubmitModal = ({ req, onClose, onDone }) => {
  const { t } = useI18n();
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (req) setF(req.submission ? { ...EMPTY, ...req.submission } : EMPTY); }, [req]);
  const up = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const n = (v) => (v === "" || v == null ? 0 : Number(v));
  const save = async () => {
    try {
      await CL.supplierSubmit(req.id, { ...f, direct: n(f.direct), indirect: n(f.indirect), carbon_price_paid: n(f.carbon_price_paid) });
      toast.success(t("submittedToast")); onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  return (
    <Modal open={!!req} onClose={onClose} title={`${t("submitData")} — ${req?.product || req?.cn_code || ""}`}
      footer={<><Btn variant="outline" onClick={onClose}>{t("cancel")}</Btn><Btn onClick={save} disabled={!f.installation_name || f.direct === ""} data-testid="sup-submit-save"><Send className="h-4 w-4" />{t("submitData")}</Btn></>}>
      <div className="grid grid-cols-2 gap-4">
        <Field label={t("installation")}><TextInput data-testid="sup-installation" value={f.installation_name} onChange={up("installation_name")} /></Field>
        <Field label={t("installationCountry")}><TextInput data-testid="sup-installation-country" value={f.installation_country} onChange={up("installation_country")} /></Field>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Field label={t("directIntensity")}><TextInput type="number" data-testid="sup-direct" value={f.direct} onChange={up("direct")} /></Field>
        <Field label={t("indirectIntensity")}><TextInput type="number" data-testid="sup-indirect" value={f.indirect} onChange={up("indirect")} /></Field>
        <Field label={t("carbonPricePaid")}><TextInput type="number" data-testid="sup-price" value={f.carbon_price_paid} onChange={up("carbon_price_paid")} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label={t("verifier")}><TextInput data-testid="sup-verifier" value={f.verifier} onChange={up("verifier")} /></Field>
        <Field label={t("verificationRef")}><TextInput data-testid="sup-verification-ref" value={f.verification_ref} onChange={up("verification_ref")} /></Field>
      </div>
      <Field label={t("commentLbl")}><TextArea data-testid="sup-comment" value={f.comment} onChange={up("comment")} /></Field>
    </Modal>
  );
};

export default function SupplierPortal() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const load = useCallback(() => {
    if (!user?.email_verified) return;
    CL.supplierInbox().then(setRows).catch(() => {});
  }, [user]);
  useEffect(() => { load(); }, [load]);
  const resend = async () => {
    try {
      const res = await CL.supplierResendVerification();
      if (res.sent === false) toast.error(t("verifySendFail"));
      else toast.success(t("verifySent"));
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  return (
    <div className="min-h-screen bg-[#F5F6F1]" data-testid="supplier-portal">
      <header className="flex items-center justify-between border-b border-[#e4e6dd] bg-white px-6 py-4">
        <div className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#14512f] text-[#D4F870]"><Leaf className="h-4 w-4" /></span>
          <span className="font-display text-lg text-[#14512f]">{t("supPortalTitle")}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden text-sm text-[#6b7268] sm:inline" data-testid="supplier-email">{user?.email}</span>
          <LangToggle />
          <Btn variant="outline" onClick={logout} data-testid="supplier-logout"><LogOut className="h-4 w-4" />{t("logout")}</Btn>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">
        <h1 className="font-display text-3xl text-[#16321f]">{t("supPortalTitle")}</h1>
        <p className="mb-8 mt-2 text-sm text-[#6b7268]">{t("supPortalDesc")}</p>
        {!user?.email_verified ? (
          <Panel data-testid="sup-verify-notice">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="font-display text-lg text-[#16321f]">{t("verifyTitle")}</div>
                <p className="mt-1 text-sm text-[#6b7268]">{t("verifyDesc")} <b>{user?.email}</b></p>
              </div>
              <Btn variant="outline" onClick={resend} data-testid="sup-verify-resend"><Send className="h-4 w-4" />{t("verifyResend")}</Btn>
            </div>
          </Panel>
        ) : rows.length === 0 ? <Panel><EmptyState text={t("noRequests")} /></Panel> : (
          <div className="space-y-4">
            {rows.map((r) => (
              <Panel key={r.id} data-testid={`sup-req-${r.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-xs text-[#8a8f83]">{t("requester")}: <span className="font-medium text-[#1b2420]">{r.requester_org}</span></div>
                    <div className="mt-1 font-display text-lg text-[#16321f]">{r.product || r.cn_code}</div>
                    <div className="text-sm text-[#4b544c]">{t("cnCode")}: {r.cn_code || "—"} · {t(`sec_${r.sector}`)} · {r.year} {r.quarter}</div>
                    {r.note && <div className="mt-2 text-sm text-[#6b7268]">{r.note}</div>}
                    {r.submission && <div className="mt-2 text-xs text-[#4b544c]">{r.submission.installation_name} · {r.submission.direct} / {r.submission.indirect} tCO₂e/t</div>}
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusPill status={r.status} />
                    {["pending", "rejected"].includes(r.status) && (
                      <Btn onClick={() => setActive(r)} data-testid={`sup-submit-${r.id}`}><Send className="h-4 w-4" />{r.status === "rejected" ? t("resubmit") : t("submitData")}</Btn>
                    )}
                  </div>
                </div>
              </Panel>
            ))}
          </div>
        )}
      </main>
      <SubmitModal req={active} onClose={() => setActive(null)} onDone={load} />
    </div>
  );
}
